import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
    RebelColors,
    RebelButton,
    RebelAccentButton,
    RebelSecondaryButton,
    RebelDangerButton,
    RebelIcon,
    RebelModal,
    RebelModalHeader,
    RebelModalBody,
    RebelModalFooter
} from '../../../../styles/rebel-experiences';
import { GetDB, generateGUID, getEnvironment } from '../../../../utils/database/firebase-budget-tool';
import { listenToDocumentProperty, updateDocumentProperty, setDocument, listenToDocument } from '../../../../utils/firestore-functions';

const RebelExperienceAgendaTab = ({ guideId, guideData }) => {
    const [agendaId, setAgendaId] = useState(null);
    const [viewMode, setViewMode] = useState('basic'); // 'basic' or 'advanced'
    const [creating, setCreating] = useState(false);
    const [unlinkModal, setUnlinkModal] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);

    const budgetDB = GetDB();

    // Listen for agendaId on guide
    useEffect(() => {
        const unsubscribe = listenToDocumentProperty(
            budgetDB,
            "rebelExperienceGuides",
            guideId,
            "agendaId",
            (value) => {
                setAgendaId(value || null);
            }
        );

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [guideId, budgetDB]);

    // Listen for viewMode on agenda when agendaId exists
    useEffect(() => {
        if (!agendaId) {
            setViewMode('basic');
            return;
        }

        const unsubscribe = listenToDocumentProperty(
            budgetDB,
            "agenda-saved-agendas",
            agendaId,
            "viewMode",
            (value) => {
                setViewMode(value || 'basic');
            }
        );

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [agendaId, budgetDB]);

    // Get villa info from experiences
    const getVillaInfo = () => {
        const experiences = guideData?.experiences?.value || {};
        return Object.values(experiences)
            .filter(exp => exp.villaId)
            .map(exp => ({
                id: exp.villaId,
                name: exp.villaName || exp.title || 'Unnamed Property'
            }));
    };

    const villaInfo = getVillaInfo();

    const createAgenda = async () => {
        setCreating(true);
        try {
            const newAgendaId = generateGUID();
            const timestamp = new Date().toISOString();

            // Get villa IDs from experiences
            const villaIds = villaInfo.map(v => v.id);

            // Build blocksPerLocation with empty arrays for each villa
            const blocksPerLocation = {};
            villaIds.forEach(id => {
                blocksPerLocation[id] = [];
            });

            const agendaData = {
                id: newAgendaId,
                agendaName: `${guideData?.title?.value || 'Untitled'} - Agenda`,
                blocksPerLocation: blocksPerLocation,
                columns: guideData?.tripLength?.value || 3,
                selectedLocationIds: villaIds,
                participantCount: guideData?.attendees?.value || 1,
                customColors: null,
                createdAt: timestamp,
                updatedAt: timestamp
            };

            // Save agenda to agenda-saved-agendas collection
            await new Promise((resolve, reject) => {
                setDocument(
                    budgetDB,
                    "agenda-saved-agendas",
                    newAgendaId,
                    agendaData,
                    () => resolve(),
                    (error) => reject(error)
                );
            });

            // Update guide with agendaId
            await new Promise((resolve, reject) => {
                updateDocumentProperty(
                    budgetDB,
                    "rebelExperienceGuides",
                    guideId,
                    "agendaId",
                    newAgendaId,
                    () => resolve(),
                    (error) => reject(error)
                );
            });

        } catch (error) {
            console.error('Error creating agenda:', error);
            alert('Error creating agenda. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const getAgendaUrl = () => {
        const baseUrl = `https://moniker-agenda.netlify.app/?id=${agendaId}`;
        return getEnvironment() === 'dev' ? `${baseUrl}&db=dev` : baseUrl;
    };

    const openAgenda = () => {
        window.open(getAgendaUrl(), '_blank');
    };

    const copyLink = () => {
        navigator.clipboard.writeText(getAgendaUrl());
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    const toggleViewMode = async (newMode) => {
        if (!agendaId) return;

        try {
            await new Promise((resolve, reject) => {
                updateDocumentProperty(
                    budgetDB,
                    "agenda-saved-agendas",
                    agendaId,
                    "viewMode",
                    newMode,
                    () => resolve(),
                    (error) => reject(error)
                );
            });
        } catch (error) {
            console.error('Error updating view mode:', error);
            alert('Error updating view mode. Please try again.');
        }
    };

    const unlinkAgenda = async () => {
        try {
            await new Promise((resolve, reject) => {
                updateDocumentProperty(
                    budgetDB,
                    "rebelExperienceGuides",
                    guideId,
                    "agendaId",
                    null,
                    () => resolve(),
                    (error) => reject(error)
                );
            });
            setUnlinkModal(false);
        } catch (error) {
            console.error('Error unlinking agenda:', error);
            alert('Error unlinking agenda. Please try again.');
        }
    };

    return (
        <React.Fragment>
            {/* Unlink Confirmation Modal */}
            <RebelModal isOpen={unlinkModal} toggle={() => setUnlinkModal(false)}>
                <RebelModalHeader toggle={() => setUnlinkModal(false)}>
                    Unlink Agenda?
                </RebelModalHeader>
                <RebelModalBody>
                    Are you sure you want to unlink this agenda from the guide?
                    <br /><br />
                    The agenda will still exist in the database and can be accessed directly via its URL,
                    but it will no longer be associated with this experience guide.
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelSecondaryButton onClick={() => setUnlinkModal(false)}>
                        Cancel
                    </RebelSecondaryButton>
                    <RebelDangerButton onClick={unlinkAgenda}>
                        <RebelIcon icon={['fas', 'unlink']} />
                        Unlink Agenda
                    </RebelDangerButton>
                </RebelModalFooter>
            </RebelModal>

            <AgendaContainer>
                <HeaderSection>
                    <SectionTitle>
                        <RebelIcon icon={['fas', 'calendar-alt']} />
                        Agenda Integration
                    </SectionTitle>
                    <SectionDescription>
                        {agendaId
                            ? 'This guide is linked to an agenda for detailed day-by-day planning.'
                            : 'Link this experience guide to an agenda for detailed day-by-day planning.'}
                    </SectionDescription>
                </HeaderSection>

                {!agendaId ? (
                    // No agenda linked
                    <ContentSection>
                        <EmptyState>
                            <RebelIcon icon={['fas', 'calendar-plus']} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                            <EmptyTitle>No agenda linked to this guide</EmptyTitle>
                            <EmptyDescription>
                                Create an agenda to plan daily activities, meals, and events for this experience.
                            </EmptyDescription>
                        </EmptyState>

                        <CreateButton onClick={createAgenda} disabled={creating || villaInfo.length === 0}>
                            <RebelIcon icon={['fas', 'plus']} />
                            {creating ? 'Creating Agenda...' : 'Create Agenda'}
                        </CreateButton>

                        {villaInfo.length === 0 && (
                            <WarningMessage>
                                <RebelIcon icon={['fas', 'exclamation-triangle']} />
                                You must add at least one property to this experience before creating an agenda.
                            </WarningMessage>
                        )}

                        <InfoBox>
                            <InfoTitle>This will create a new agenda with:</InfoTitle>
                            <InfoList>
                                <InfoItem>
                                    <RebelIcon icon={['fas', 'calendar-day']} />
                                    <span>{guideData?.tripLength?.value || 0} days</span>
                                </InfoItem>
                                <InfoItem>
                                    <RebelIcon icon={['fas', 'users']} />
                                    <span>{guideData?.attendees?.value || 0} participants</span>
                                </InfoItem>
                                <InfoItem>
                                    <RebelIcon icon={['fas', 'home']} />
                                    <span>
                                        {villaInfo.length > 0
                                            ? `Properties: ${villaInfo.map(v => v.name).join(', ')}`
                                            : 'No properties selected yet'}
                                    </span>
                                </InfoItem>
                            </InfoList>
                        </InfoBox>
                    </ContentSection>
                ) : (
                    // Agenda exists
                    <ContentSection>
                        <AgendaCard>
                            <AgendaIdSection>
                                <AgendaIdLabel>Agenda ID</AgendaIdLabel>
                                <AgendaIdValue>{agendaId}</AgendaIdValue>
                            </AgendaIdSection>

                            <ButtonGroup>
                                <RebelAccentButton onClick={openAgenda}>
                                    <RebelIcon icon={['fas', 'external-link-alt']} />
                                    Open in Agenda App
                                </RebelAccentButton>
                                <RebelSecondaryButton onClick={copyLink}>
                                    <RebelIcon icon={['fas', 'copy']} />
                                    {copySuccess ? 'Copied!' : 'Copy Link'}
                                </RebelSecondaryButton>
                            </ButtonGroup>
                        </AgendaCard>

                        <SideBySideRow>
                            <ViewModeRow>
                                <ViewModeLabel>
                                    <RebelIcon icon={['fas', 'eye']} />
                                    View Mode
                                </ViewModeLabel>
                                <ToggleSwitch>
                                    <ToggleOption
                                        active={viewMode === 'basic'}
                                        onClick={() => toggleViewMode('basic')}
                                    >
                                        <RebelIcon icon={['fas', 'th-large']} />
                                        Basic
                                    </ToggleOption>
                                    <ToggleOption
                                        active={viewMode === 'advanced'}
                                        onClick={() => toggleViewMode('advanced')}
                                    >
                                        <RebelIcon icon={['fas', 'th-list']} />
                                        Advanced
                                    </ToggleOption>
                                </ToggleSwitch>
                            </ViewModeRow>

                            {villaInfo.length > 0 && (
                                <PropertiesSection>
                                    <PropertiesTitle>Properties included:</PropertiesTitle>
                                    <PropertiesList>
                                        {villaInfo.map((villa, index) => (
                                            <PropertyItem key={index}>
                                                <RebelIcon icon={['fas', 'home']} />
                                                <span>{villa.name}</span>
                                            </PropertyItem>
                                        ))}
                                    </PropertiesList>
                                </PropertiesSection>
                            )}
                        </SideBySideRow>

                        <UnlinkSection>
                            <RebelDangerButton onClick={() => setUnlinkModal(true)}>
                                <RebelIcon icon={['fas', 'unlink']} />
                                Unlink Agenda
                            </RebelDangerButton>
                        </UnlinkSection>
                    </ContentSection>
                )}
            </AgendaContainer>
        </React.Fragment>
    );
};

export default RebelExperienceAgendaTab;

// Styled Components
const AgendaContainer = styled.div`
    padding: 24px;
`;

const HeaderSection = styled.div`
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid ${RebelColors.gold}40;
`;

const SectionTitle = styled.h3`
    color: ${RebelColors.gold};
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
        color: ${RebelColors.gold};
    }
`;

const SectionDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const SideBySideRow = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;

    & > * {
        flex: 1;
        min-width: 250px;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 40px 20px;
    color: ${RebelColors.primaryMuted};
    background: rgba(255, 255, 255, 0.02);
    border-radius: 12px;
    border: 2px dashed ${RebelColors.border};
`;

const EmptyTitle = styled.h4`
    color: ${RebelColors.text};
    font-size: 18px;
    margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
    color: ${RebelColors.primaryMuted};
    font-size: 14px;
    margin: 0;
`;

const CreateButton = styled(RebelAccentButton)`
    align-self: center;
    padding: 14px 32px;
    font-size: 16px;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const WarningMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid rgba(251, 191, 36, 0.3);
    border-radius: 8px;
    color: ${RebelColors.warning};
    font-size: 14px;

    svg {
        color: ${RebelColors.warning};
        flex-shrink: 0;
    }
`;

const InfoBox = styled.div`
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    border-radius: 12px;
    padding: 20px;
`;

const InfoTitle = styled.h5`
    color: ${RebelColors.primaryMuted};
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 16px 0;
`;

const InfoList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const InfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${RebelColors.text};
    font-size: 14px;

    svg {
        color: ${RebelColors.gold};
        width: 16px;
    }
`;

const AgendaCard = styled.div`
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.gold}40;
    border-radius: 12px;
    padding: 24px;
`;

const AgendaIdSection = styled.div`
    margin-bottom: 20px;
`;

const AgendaIdLabel = styled.div`
    color: ${RebelColors.primaryMuted};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
`;

const AgendaIdValue = styled.div`
    color: ${RebelColors.text};
    font-family: monospace;
    font-size: 14px;
    background: ${RebelColors.background};
    padding: 10px 14px;
    border-radius: 6px;
    border: 1px solid ${RebelColors.border};
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const PropertiesSection = styled.div`
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    border-radius: 12px;
    padding: 20px;
`;

const PropertiesTitle = styled.h5`
    color: ${RebelColors.primaryMuted};
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 16px 0;
`;

const PropertiesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PropertyItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${RebelColors.text};
    font-size: 14px;

    svg {
        color: ${RebelColors.gold};
        font-size: 12px;
    }
`;

const UnlinkSection = styled.div`
    padding-top: 16px;
    border-top: 1px solid ${RebelColors.border};
`;

const ViewModeRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: ${RebelColors.surface};
    border: 1px solid ${RebelColors.border};
    border-radius: 10px;
    padding: 16px 20px;
`;

const ViewModeLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${RebelColors.text};
    font-size: 14px;
    font-weight: 500;

    svg {
        color: ${RebelColors.gold};
        font-size: 16px;
    }
`;

const ToggleSwitch = styled.div`
    display: flex;
    width: fit-content;
    background: ${RebelColors.background};
    border: 1px solid ${RebelColors.border};
    border-radius: 8px;
    padding: 3px;
`;

const ToggleOption = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.active
        ? RebelColors.gold
        : 'transparent'
    };
    color: ${props => props.active ? '#000000' : RebelColors.primaryMuted};

    svg {
        font-size: 12px;
        color: ${props => props.active ? '#000000' : RebelColors.primaryMuted};
    }

    &:hover {
        color: ${props => props.active ? '#000000' : RebelColors.text};

        svg {
            color: ${props => props.active ? '#000000' : RebelColors.text};
        }
    }
`;
